import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, TrendingUp, TrendingDown, Layers, Sparkles } from "lucide-react";
import { getDiscover, getCompanies, getCompany } from "../lib/api";
import { CompanyCard } from "../components/CompanyCard";
import { SectionHeading, SectionDivider } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { DiscoverSearch } from "../components/discover/DiscoverSearch";
import { FeaturedDiscoveries } from "../components/discover/FeaturedDiscoveries";
import { ResearchPath } from "../components/discover/ResearchPath";
import { PageLoading } from "./Markets";
import { formatPercent, signColor } from "../lib/format";
import { DataState } from "../components/DataState";

const SectorCard = ({ s }) => (
  <Link
    to={`/companies?sector=${s.id}`}
    data-testid={`discover-sector-${s.id}`}
    className="lv-panel lv-panel-hover flex items-center justify-between gap-3 p-3.5 transition-transform duration-200 hover:-translate-y-0.5"
  >
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-white">{s.name}</div>
      <div className="truncate text-xs text-zinc-500">{s.detail}</div>
    </div>
    <span className={`shrink-0 font-mono text-sm ${signColor(s.change_percent)}`}>{formatPercent(s.change_percent)}</span>
  </Link>
);

export default function Discover() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [universe, setUniverse] = useState(null);

  const load = () => { setError(false); getDiscover().then(setData).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let active = true;
    getCompanies()
      .then((d) => Promise.all(d.companies.map((c) => getCompany(c.ticker).then((full) => ({ ...c, scores: full.scores })).catch(() => c))))
      .then((withScores) => { if (active) setUniverse(withScores); })
      .catch(() => { if (active) setUniverse([]); });
    return () => { active = false; };
  }, []);

  if (error) return <DataState onRetry={load} />;
  if (!data) return <PageLoading />;

  return (
    <div className="space-y-10">
      <div className="lv-fade-up border-b border-white/[0.07] pb-6">
        <div className="mb-2 flex items-center gap-2">
          <span className="h-1 w-1 rounded-full bg-amber-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400/80">LONGVIEW Discovery Engine</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">Discover the market differently.</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">
          Explore businesses, uncover patterns, and find companies worth understanding — every signal here is explainable, never a hot tip.
        </p>
      </div>

      <DiscoverSearch companies={universe} sectors={data.sectors} />

      <Reveal>
        <SectionHeading kicker="Explore By" title="Companies worth researching" icon={Sparkles} />
        <FeaturedDiscoveries companies={universe} />
      </Reveal>

      <SectionDivider />

      <Reveal>
        <SectionHeading kicker="Sectors" title="Where the money is moving" icon={Layers} />
        <StaggerGrid data-testid="discover-filter-sectors" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.sectors.map((s) => <StaggerItem key={s.id}><SectorCard s={s} /></StaggerItem>)}
        </StaggerGrid>
      </Reveal>

      <Reveal>
        <SectionHeading kicker="Market Movers" title="Largest moves today" icon={Compass}
          right={<Link to="/companies?filter=movers" className="font-mono text-xs text-amber-400 transition-colors hover:text-amber-300" data-testid="discover-filter-movers">View all →</Link>} />
        <StaggerGrid className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.top_movers.map((c) => <StaggerItem key={c.ticker}><CompanyCard c={c} /></StaggerItem>)}
        </StaggerGrid>
      </Reveal>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Reveal>
          <SectionHeading kicker="Momentum" title="Companies gaining momentum" icon={TrendingUp}
            right={<Link to="/companies?filter=momentum" className="font-mono text-xs text-emerald-400 hover:text-emerald-300" data-testid="discover-filter-momentum">More →</Link>} />
          <div className="space-y-3">
            {data.momentum.map((c) => <MoverRow key={c.ticker} c={c} up />)}
          </div>
        </Reveal>
        <Reveal delay={80}>
          <SectionHeading kicker="Under Pressure" title="Companies under pressure" icon={TrendingDown}
            right={<Link to="/companies?filter=under_pressure" className="font-mono text-xs text-rose-400 hover:text-rose-300" data-testid="discover-filter-under-pressure">More →</Link>} />
          <div className="space-y-3">
            {data.under_pressure.map((c) => <MoverRow key={c.ticker} c={c} />)}
          </div>
        </Reveal>
      </div>

      <ResearchPath />
    </div>
  );
}

const MoverRow = ({ c }) => (
  <Link to={`/company/${c.ticker}`} className="lv-panel lv-panel-hover flex items-center gap-3 p-3.5 transition-transform duration-200 hover:-translate-y-0.5">
    <div className="flex h-9 w-14 items-center justify-center rounded border border-white/10 bg-[#0B0C0E] font-mono text-[11px] font-semibold text-zinc-300">{c.ticker}</div>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium text-white">{c.name}</div>
      <div className="truncate text-xs text-amber-100/70">{c.discover_reason}</div>
    </div>
    <span className={`shrink-0 font-mono text-sm ${signColor(c.change_percent)}`}>{formatPercent(c.change_percent)}</span>
  </Link>
);
